import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useRide } from '../contexts/RideContext';
import { Home, Search, DollarSign, Wallet, User as UserIcon, LogOut, Menu, X, ShieldCheck } from 'lucide-react';
import { Agent } from '@ouagaliv/shared';

const navItems = [
  { path: '/', label: 'Accueil', icon: Home },
  { path: '/available', label: 'Courses', icon: Search },
  { path: '/earnings', label: 'Gains', icon: DollarSign },
  { path: '/recharge', label: 'Recharge', icon: Wallet },
  { path: '/profile', label: 'Profil', icon: UserIcon },
];

export default function Layout() {
  const { user, logout, updateAvailability, updateLocation } = useAuth();
  const { fetchAvailableRides } = useRide();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const agentUser = user as Agent | null;
  const [isAvailable, setIsAvailable] = useState(agentUser?.isAvailable || false);
  const isActive = agentUser?.status === 'active';
  const lastLocationUpdateRef = useRef(0);

  useEffect(() => {
    setIsAvailable(agentUser?.isAvailable || false);
  }, [agentUser?.isAvailable]);

  // Tant que l'agent est disponible, on suit sa position réelle (throttlée à 15s)
  // et on rafraîchit automatiquement les courses proches, sur toutes les pages.
  useEffect(() => {
    if (!agentUser || !isAvailable || !('geolocation' in navigator)) return;

    const watchId = navigator.geolocation.watchPosition(
      pos => {
        const now = Date.now();
        if (now - lastLocationUpdateRef.current < 15000) return;
        lastLocationUpdateRef.current = now;
        updateLocation(pos.coords.latitude, pos.coords.longitude);
      },
      () => {},
      { enableHighAccuracy: true, maximumAge: 10000, timeout: 15000 }
    );
    return () => navigator.geolocation.clearWatch(watchId);
  }, [agentUser?.id, isAvailable]);

  useEffect(() => {
    if (!agentUser || !isAvailable || !agentUser.currentLat || !agentUser.currentLng) return;
    fetchAvailableRides(agentUser.currentLat, agentUser.currentLng, 3000);
    const interval = setInterval(() => {
      fetchAvailableRides(agentUser.currentLat!, agentUser.currentLng!, 3000);
    }, 10000);
    return () => clearInterval(interval);
  }, [agentUser?.id, isAvailable, agentUser?.currentLat, agentUser?.currentLng]);

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header className="bg-white border-b border-border sticky top-0 z-40">
        <div className="container h-16 flex items-center justify-between">
          <Link to="/" className="font-bold text-xl text-primary" style={{textDecoration: 'none'}}>
            OuagaLiv Agent
          </Link>
          
          <nav className={`fixed inset-0 bg-background z-50 flex flex-col p-6 transition-all duration-300 ${isMenuOpen ? 'translate-x-0' : 'translate-x-full'}`}>
            <div className="flex justify-between items-center mb-8">
              <Link to="/" className="font-bold text-xl text-primary" style={{textDecoration: 'none'}}>OuagaLiv Agent</Link>
              <button onClick={() => setIsMenuOpen(false)} className="p-2 rounded-lg hover:bg-gray-100"><X size={24} /></button>
            </div>
            <ul className="space-y-4">
              {navItems.map(item => (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    onClick={() => setIsMenuOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg text-lg font-medium transition-colors ${
                      location.pathname === item.path || (item.path !== '/' && location.pathname.startsWith(item.path))
                        ? 'bg-primary/10 text-primary'
                        : 'text-text hover:bg-gray-50'
                    }`}
                  >
                    <item.icon size={24} />
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
            <div className="mt-auto pt-6 border-t border-border">
              {!isActive && (
                <Link
                  to="/onboarding"
                  onClick={() => setIsMenuOpen(false)}
                  className="flex items-center gap-2 p-3 mb-4 rounded-lg bg-amber-50 text-amber-800 text-sm font-medium"
                  style={{ textDecoration: 'none' }}
                >
                  <ShieldCheck size={18} /> Activer mon compte
                </Link>
              )}
              <div className="flex items-center justify-between mb-4">
                <span className="font-medium text-text">Disponibilité</span>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isAvailable}
                    onChange={e => { setIsAvailable(e.target.checked); updateAvailability(e.target.checked); }}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/30 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                </label>
              </div>
              <p className="text-sm text-text-secondary mb-2">Connecté en tant que</p>
              <p className="font-medium text-text">{user?.fullName}</p>
              <p className="text-xs text-text-secondary capitalize">{agentUser?.vehicleType || ''}</p>
              <button onClick={logout} className="btn btn-outline btn-full mt-4">
                <LogOut size={18} /> Déconnexion
              </button>
            </div>
          </nav>

          <button
            className="md:hidden p-2 rounded-lg hover:bg-gray-100"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label={isMenuOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </header>

      <main className="flex-1 overflow-auto pb-24">
        <Outlet />
      </main>

      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-border z-40">
        <div className="grid grid-cols-5 max-w-lg mx-auto">
          {navItems.map(item => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex flex-col items-center gap-1 px-2 py-3 transition-colors ${
                location.pathname === item.path || (item.path !== '/' && location.pathname.startsWith(item.path))
                  ? 'text-primary'
                  : 'text-text-secondary hover:text-primary'
              }`}
            >
              <item.icon size={24} />
              <span className="text-xs font-medium">{item.label}</span>
            </Link>
          ))}
        </div>
      </nav>
    </div>
  );
}